import { TrackerDiv } from "@/components/Tracker";
import { PRODUCT_TYPE } from "@/constants/gaodingVip";
import { PermissionCodes } from "@/constants/permissionCodes";
import useAppStore from "@/context/store";
import { isPersonalOrg } from "@/utils/gaodingVip";
import { OrgService } from "@/utils/gd-org";
import { useHomeUrl } from "@/utils/getHomeUrl";
import { gaodingEditHost, huabanArtHost } from "@/utils/host";
import useSideNavCollapse from "@/utils/useSideNavCollapse";
import classNames from "classnames";
import React from "react";
import ContactServiceQRCodeModal from "../ContactServiceQRCodeModal";
import HBIcon from "../HBIcon";
import LogoWithOrg from "../LogoWithOrg";
import DiscoveryBoardGuide from "./DiscoveryBoardGuide";
import styles from "./index.module.less";
import NavItem from "./NavItem";
import SideNavBanner from "./SideNavBanner";

// 企业版付费触点角标，从稿定复制
const EnterpriseBadge = () => (
  <div
    style={{
      color: "rgb(195, 169, 125)",
      background: "rgb(255, 255, 255)",
      border: "1px solid rgba(195, 169, 125, 0.4)",
      borderRadius: "2px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      padding: "3px 1px",
      fontSize: "12px",
      transform: "scale(0.85)",
      fontWeight: 600,
      height: "19px",
      width: "45px",
      justifyContent: "center",
      textIndent: 0,
    }}
  >
    企业版
  </div>
);

export default function SideNav() {
  const [isCollapsed, setIsCollapsed] = useSideNavCollapse();
  const user = useAppStore((state) => state.user);
  const orgInfo = useAppStore((state) => state.orgInfo);
  const currentOrg = useAppStore((state) => state.currentOrg);
  const [showOrgQrModal, setShowOrgQrModal] = React.useState(false);
  const homeUrl = useHomeUrl(user, { allowHome: false });
  const orgService = OrgService.getInstance();

  // productType
  const productType = React.useMemo(() => {
    return (
      currentOrg?.product?.base_product_type ||
      currentOrg?.product?.product_type
    );
  }, [currentOrg]);

  // 企业资源库入口点击
  const handleEnterpriseResourceClick = () => {
    // 付费触点
    if (enterpriseResourceGuideVisible) {
      setShowOrgQrModal(true);
      return;
    }

    window.open(`https://${gaodingEditHost}/dam/orgs/`, "_blank");
  };

  // 判断是否显示企业资源入口
  const enterpriseResourceVisible = React.useMemo(() => {
    if (!user || !currentOrg) return false;
    return [PRODUCT_TYPE.PROFESSIONAL, PRODUCT_TYPE.FLAGSHIP].includes(
      productType
    );
  }, [user, currentOrg, productType]);

  // 团队空间入口点击
  const handleTeamSpaceClick = () => {
    window.open(`https://${gaodingEditHost}/dam/teams`, "_blank");
  };

  // 判断是否显示团队空间入口
  const teamSpaceVisible = React.useMemo(() => {
    if (!user || !currentOrg) return false;
    return [
      PRODUCT_TYPE.FREE,
      PRODUCT_TYPE.BASIC,
      PRODUCT_TYPE.PROFESSIONAL,
    ].includes(productType);
  }, [user, currentOrg, productType]);

  // 付费触点标识
  const enterpriseResourceGuideVisible = React.useMemo(() => {
    return currentOrg && productType === PRODUCT_TYPE.BASIC; // 未付费的团队版才展示付费触点
  }, [currentOrg, productType]);

  const contentItemGroups = [
    [
      {
        id: "sidenav-item-inspiration",
        title: "灵感",
        icon: "hb_inspiration",
        path: homeUrl,
        customActiveCheck: (path) =>
          ["/discovery", "/follow", "/guide"].some((p) => path.startsWith(p)),
      },
      {
        id: "sidenav-item-user",
        title: "我的",
        icon: "hb_mine",
        path: user ? `/space` : null,
        loginRequired: true,
      },
    ],
    [
      {
        id: "sidenav-item-create",
        title: "发布",
        icon: "hb_add",
        path: "/create",
        loginRequired: true,
      },
      {
        title: "企业资源",
        icon: "hb_folder",
        path: `https://${gaodingEditHost}/dam/orgs/`,
        openInNewTab: true,
        visible: enterpriseResourceVisible,
        onClick: handleEnterpriseResourceClick,
        extraElement: enterpriseResourceGuideVisible ? (
          <EnterpriseBadge /> // 付费触点标识
        ) : null,
      },
      {
        title: "素材",
        icon: "style",
        path: "/materials",
      },
      {
        title: "创作",
        icon: "hb_template",
        path: `https://${gaodingEditHost}`,
        openInNewTab: true,
      },
      {
        title: "稿定 AI",
        icon: "hb_ai_creative",
        path: `https://${huabanArtHost}/utms/96bc59da5f384a46983b1b891eae0358`,
        openInNewTab: true,
      },
    ],
    [
      {
        title: "团队空间",
        icon: "hb_team",
        path: `https://${gaodingEditHost}/dam/teams`,
        openInNewTab: true,
        visible: teamSpaceVisible,
        onClick: handleTeamSpaceClick,
      },
    ],
  ];

  const footerItems = [
    {
      title: "邀请成员",
      icon: "hb_add_member",
      path: null,
      onClick: () => orgService.inviteMember(),
      visible: Boolean(user && !isPersonalOrg(orgInfo?.org)),
      permission: PermissionCodes.inviteMemberMenu,
    },
    {
      title: "浏览器插件",
      icon: "hb_plugin",
      path: "/about/goodies",
      openInNewTab: true,
      permission: PermissionCodes.pluginHuaban,
    },
    {
      title: "下载APP",
      icon: "hb_download",
      path: "/apps",
      openInNewTab: true,
      permission: PermissionCodes.downloadHuabanApp,
    },
  ];

  const hasPermission = (permission) => {
    if (!permission) return true;
    return useAppStore.getState().userPermissions[permission];
  };

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.collapsed]: isCollapsed,
      })}
    >
      <nav className={classNames(styles.sideNav)}>
        <div className={styles.header}>
          <LogoWithOrg isCollapsed={isCollapsed} />
        </div>
        <div className={classNames(styles.content, styles.navSection)}>
          {contentItemGroups.map((groupItems, index) => (
            <React.Fragment key={index}>
              {groupItems.map((item, index) => {
                if (item.visible === false) return null;
                return (
                  <NavItem
                    id={item.id}
                    key={item.path}
                    icon={item.icon}
                    title={item.title}
                    link={item.path}
                    openInNewTab={item.openInNewTab}
                    isCollapsed={isCollapsed}
                    onClick={item.onClick}
                    loginRequired={item.loginRequired}
                    customActiveCheck={item.customActiveCheck}
                    extraElement={item.extraElement}
                    index={index}
                  />
                );
              })}
              {index < contentItemGroups.length - 1 &&
                contentItemGroups[index + 1].some(
                  (item) => item.visible !== false
                ) && <div className={styles.divider} />}
            </React.Fragment>
          ))}
        </div>
        <div className={classNames(styles.footer, styles.navSection)}>
          <SideNavBanner isCollapsed={isCollapsed} />
          {footerItems.map((item, index) => {
            if (!hasPermission(item.permission)) return null;
            if (item.visible === false) return null;
            return (
              <NavItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                link={item.path}
                openInNewTab={item.openInNewTab}
                onClick={item.onClick}
                isCollapsed={isCollapsed}
                extraElement={item.extraElement}
                index={index}
              />
            );
          })}
        </div>
      </nav>

      {/* 展开收起按钮 */}
      <TrackerDiv
        id="sidenav-collapse-trigger"
        className={styles.collapseButtonTrigger}
      >
        <button
          className={styles.collapseButton}
          data-tooltip={isCollapsed ? "展开侧边栏" : "收起侧边栏"}
          data-tooltip-position="top-right"
          data-gd-click="button_click"
          data-module-name="左侧导航栏"
          data-subject-name="按钮"
          data-apply-client="使用端"
          data-button-name={isCollapsed ? "展开导航" : "收起导航"}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <HBIcon type="a-Polygon7" />
        </button>
      </TrackerDiv>

      {/* 基于画板的灵感页引导 */}
      <DiscoveryBoardGuide />

      {/* 企业资源二维码弹窗 */}
      <ContactServiceQRCodeModal
        visible={showOrgQrModal}
        onClose={() => setShowOrgQrModal(false)}
      />
    </div>
  );
}
